import { createApi } from '@reduxjs/toolkit/query/react';
import { customFetchBase } from './helpers/customFetchBase.js';
import { type Announcement, type BaseAnnouncement, type UserAnnouncement } from '@soulhx/fs-common';
import { providesList } from './helpers/rtk-helpers.js';

export interface GetAnnouncementsParams {
  page?: number;
}

export interface DeleteAnnouncementResponse {
  success: boolean;
}

export interface SearchAnnouncementsParams {
  page?: number;
  searchText?: string;
}

export const announcementsApi = createApi({
  reducerPath: 'announcements',
  baseQuery: customFetchBase,
  tagTypes: ['announcements', 'announcement-snapshot'],
  endpoints: (builder) => ({
    newAnnouncement: builder.mutation<Announcement, BaseAnnouncement>({
      query: (body) => ({
        url: '/comms/announcement',
        body: body,
        method: 'POST',
      }),
      invalidatesTags: ['announcements', 'announcement-snapshot'],
    }),
    getAllAnnouncements: builder.query<UserAnnouncement[], GetAnnouncementsParams>({
      query: (params) => (params.page ? `/comms/announcement?p=${params.page}` : '/comms/announcement'),
      providesTags: (result) => providesList(result, 'announcements'),
    }),
    getAnnouncementSnapshot: builder.query<UserAnnouncement[], GetAnnouncementsParams>({
      query: (params) => `/comms/announcement?u=t${params.page ? '&p=' + params.page : ''}`,
      providesTags: (result) => providesList(result, 'announcement-snapshot'),
    }),
    getAnnouncementById: builder.query<UserAnnouncement, string>({
      query: (id) => `/comms/announcement/${id}`,
      providesTags: (result) => (result ? providesList([result], 'announcements', { returnList: false }) : []),
    }),
    searchAnnouncements: builder.mutation<UserAnnouncement[], SearchAnnouncementsParams>({
      query: (body) => ({
        url: `/comms/announcement/search${body.page ? '?p=' + body.page : ''}`,
        body: { searchText: body.searchText },
        method: 'POST',
      }),
    }),
    deleteAnnouncement: builder.mutation<DeleteAnnouncementResponse, string>({
      query: (id) => ({ url: `/comms/announcement/${id}`, method: 'DELETE' }),
      invalidatesTags: ['announcements', 'announcement-snapshot'],
    }),
    updateAnnouncement: builder.mutation<Announcement, Announcement>({
      query: (item) => ({
        url: `/comms/announcement/${item.id}`,
        method: 'PUT',
        body: item,
      }),
      invalidatesTags: ['announcements', 'announcement-snapshot'],
    }),
    markAnnouncementRead: builder.mutation<UserAnnouncement, string>({
      query: (id) => ({
        url: `/comms/announcement/markRead/${id}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, params) => {
        if (error || !result) return [];

        return [
          { id: params, type: 'announcements' },
          { id: params, type: 'announcement-snapshot' },
        ];
      },
    }),
    markAllAnnouncementsRead: builder.mutation<void, void>({
      query: () => ({
        url: '/comms/announcement/markAllRead',
        method: 'POST',
      }),
      invalidatesTags: ['announcements', 'announcement-snapshot'],
    }),
  }),
});

export const {
  useNewAnnouncementMutation,
  useLazyGetAllAnnouncementsQuery,
  useDeleteAnnouncementMutation,
  useUpdateAnnouncementMutation,
  useGetAllAnnouncementsQuery,
  useMarkAnnouncementReadMutation,
  useGetAnnouncementByIdQuery,
  useSearchAnnouncementsMutation,
  useGetAnnouncementSnapshotQuery,
  useMarkAllAnnouncementsReadMutation,
} = announcementsApi;
